import React, { useCallback } from 'react';

import { Button, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { Icon, IconTypes } from '@eika/icon';

import CenteredModalContent from '../../components/shared/CenteredModalContent/CenteredModalContent';
import { useAvtaleGiroProposalDelete } from '../../fetchHooks/useAvtalegiro';
import type { AvtaleGiroProposalId } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { paymentTrackIDs } from '../../utils/gtm/trackIDs/payment';

interface Props {
    proposalId: AvtaleGiroProposalId;
    onCancel: () => void;
    onDelete: () => void;
    showDialogue: boolean;
}

const DeleteAvtaleGiroProposal: React.FC<Props> = ({ proposalId, onCancel, onDelete, showDialogue }) => {
    const translated = useTranslation();
    const infoMessage = translated.avtaleGiroProposals.deletemessages;

    const deleteproposal = useAvtaleGiroProposalDelete();

    const deleteProposalObject = useCallback(() => {
        deleteproposal.mutateAsync(proposalId).then(onDelete);
    }, [deleteproposal, proposalId, onDelete]);

    if (deleteproposal.isSuccess) {
        return (
            <CenteredModalContent withMessage>
                <CheckmarkAnimation
                    className="transfer-success-checkmark"
                    checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE}
                />
                <h3 className="typography-h3">{infoMessage.success}</h3>
            </CenteredModalContent>
        );
    }
    if (deleteproposal.error) {
        return (
            <CenteredModalContent withMessage>
                <Icon className="wallet-questionmark" type={IconTypes.WALLET_QUESTIONMARK} />
                <h3 className="typography-h3">{infoMessage.error.heading}</h3>
                <p>{infoMessage.error.description}</p>
                <Button onClick={onCancel} size="small">
                    {translated.ok}
                </Button>
            </CenteredModalContent>
        );
    }

    return (
        <CenteredModalContent>
            <ConfirmationDialog
                title={translated.avtaleGiroProposals.sureYouWantToDelete}
                id={`delete-avtalegiro-proposal-${proposalId}`}
                className="delete-avtalegiro-proposal"
                footer={
                    <div>
                        <LinkButton
                            onClick={onCancel}
                            disabled={deleteproposal.isPending}
                            data-track-id={paymentTrackIDs.avtalegiro.proposalDelete}
                            data-track-linkname={translated.cancel}
                        >
                            {translated.cancel}
                        </LinkButton>
                        <ButtonWithSpinner
                            size="small"
                            buttonType="positive"
                            onClick={deleteProposalObject}
                            showSpinner={deleteproposal.isPending}
                            data-track-id={paymentTrackIDs.avtalegiro.proposalDelete}
                            data-track-linkname="submit"
                        >
                            {translated.confirmDelete}
                        </ButtonWithSpinner>
                    </div>
                }
                show={showDialogue}
                onExitFocus={() => onCancel()}
                headingLevel={3}
            />
        </CenteredModalContent>
    );
};

export default DeleteAvtaleGiroProposal;
